import { formatWithSymbolAndCode } from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import pluralize from 'pluralize';

import {
  PlanPricing,
  PlanMetadata,
  PricingPlanType,
  MoneyDTO,
} from '../../../common/api/types';
import { FormFamily } from '../types';

export const isAnotherFamiliesProductSelected = (families: FormFamily[]) => {
  const familyWithSelectedSolution = families.find(({ solutions }) =>
    solutions.some(({ item: { selectedTier } }) => !!selectedTier)
  );
  const familyWithSelectedAddon = families.find(({ addons }) =>
    addons.some(({ item: { selectedTier } }) => !!selectedTier)
  );

  return (
    (familyWithSelectedSolution && !familyWithSelectedSolution.selected) ||
    (familyWithSelectedAddon && !familyWithSelectedAddon.selected)
  );
};

export const costDerivation = (
  pricingComponents: Record<string, PlanPricing[]>,
  meta: PlanMetadata,
  isTrial: boolean,
  pricingPlanType: PricingPlanType,
  pricing: PlanPricing,
  additionalSkuCostObject?: MoneyDTO
): { displayPrice: string; features: string[] } => {
  const { sku, productCount, billingFrequency } = meta;

  if (isTrial)
    return {
      displayPrice: formatWithSymbolAndCode(
        0,
        pricing?.originalPriceAfterDiscount?.currency,
        pricing?.originalPriceAfterDiscount?.precision
      ),
      features: [`${productCount} ${sku}`],
    };

  let basePrice: string | null = null;
  let slabFeaturesArray: string[] = [];
  let slabCount: number = 0;
  let flatSlabAmount: string | null = null;
  let features: string[] = [];

  if (
    pricingPlanType === 'BASE' ||
    pricingPlanType === 'BASE_USAGE' ||
    pricingPlanType === 'BASE_USAGE_SLAB'
  ) {
    if (pricing.originalPriceAfterDiscount) {
      const { amount, currency, precision } =
        pricing.originalPriceAfterDiscount;
      if (amount > 0) {
        basePrice = `${formatWithSymbolAndCode(
          amount,
          currency,
          precision
        )}/${billingFrequency}`;
        features = [
          `${productCount} ${sku} included`,
          additionalSkuCostObject
            ? `Additional ${sku} ${formatWithSymbolAndCode(
                additionalSkuCostObject.amount,
                additionalSkuCostObject.currency,
                additionalSkuCostObject.precision
              )} / ${pluralize.singular(sku)}`
            : '',
        ];
      }
    }
  }

  if (pricingPlanType !== 'BASE' && pricingPlanType !== 'BASE_USAGE') {
    features = [];
    if (pricingComponents && pricingComponents[sku]?.length) {
      for (const skuItem of pricingComponents[sku]) {
        if (
          (skuItem.threshold?.from !== undefined &&
            skuItem.threshold?.from !== null) ||
          (skuItem.originalPrice && skuItem.originalPrice?.amount > 0)
        ) {
          slabCount++;
          if (skuItem.originalPrice)
            flatSlabAmount =
              formatWithSymbolAndCode(
                skuItem.originalPrice?.amount,
                skuItem.originalPrice?.currency,
                skuItem.originalPrice?.precision
              ) + ` per ${pluralize.singular(sku)}`;
          if (!basePrice) {
            const thresholdTo = skuItem.threshold?.to
              ? ` - ${skuItem.threshold.to}`
              : '+';
            features.push(
              `${skuItem.threshold?.from}${thresholdTo} ${
                skuItem.name
              }: ${flatSlabAmount?.replace('per', '/')}`
            );
          } else {
            if (
              skuItem.threshold?.from === 0 &&
              skuItem.originalPrice?.amount === 0 &&
              skuItem.threshold?.to
            ) {
              features.push(
                `Includes ${skuItem.threshold?.to} ${skuItem.name} / ${billingFrequency}`
              );
            } else {
              const thresholdTo = skuItem.threshold?.to
                ? ` - ${skuItem.threshold.to}`
                : '+';
              features.push(
                `${skuItem.threshold?.from}${thresholdTo} ${
                  skuItem.name
                }: ${flatSlabAmount?.replace('per', '/')}`
              );
            }
          }
        }
      }
    }
  }

  if (basePrice && slabCount > 0)
    // BASE_USAGE_SLAB
    return { displayPrice: basePrice, features };
  else if (basePrice && slabCount === 0)
    // BASE
    return { displayPrice: basePrice, features };
  else if (!basePrice && slabCount === 1) {
    // USAGE_FLAT
    slabFeaturesArray.push(`Flat rate ${flatSlabAmount?.replace('per', '/')}`);
    return { displayPrice: flatSlabAmount || '', features: slabFeaturesArray };
  } else if (!basePrice && slabCount > 1)
    //USAGE_SLAB
    return { displayPrice: 'Per Call', features };
  else return { displayPrice: '', features };
};
